import { sortBy } from 'lodash'
import type { DropdownOption } from '../components/UI/multi-select-dropdown/MultiSelectDropdown'
import { gapAnalysisActions, gapAnalysisFiltersActions } from '../redux/actions'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import type { GAViewFilters } from '../redux/slices/gap-analysis-filters-slice'
import { fieldNames } from '../redux/slices/gap-analysis-filters-slice'
import { useGapAnalysisViews } from './useGapAnalysisViews'

export const useGAViewsManagement = () => {
  const dispatch = useAppDispatch()
  const gapAnalysisSliceFilters = useAppSelector(state => state.gapAnalysisFilters)

  const { views: gapAnalysisViews } = useGapAnalysisViews()

  const handleRemoveSelectedGAView = () => {
    dispatch(
      gapAnalysisFiltersActions.setViewFilters({
        columns: gapAnalysisSliceFilters.columns,
        competencies: [],
        hierarchies: [],
        hierarchyViewMode: { text: 'All', value: 'all' },
        managers: [],
        occupations: [],
        people: [],
        showAllReports: false,
        showGridSeparator: true,
        gridlinesFrequency: 1,
        showManagersColumn: false
      })
    )

    dispatch(gapAnalysisActions.setSelectedView(undefined))
  }

  const handleSelectGAView = (selection?: DropdownOption) => {
    // Removing by clicking on the bean triggers this, thus this "hack" is needed
    if (!selection) {
      handleRemoveSelectedGAView()
      return
    }

    // step 1: set the selected view
    dispatch(gapAnalysisActions.setSelectedView(selection))
    const selectedViewFilters = gapAnalysisViews.find(view => view.id === selection.value)?.filters || {}
    const selectedViewDisplaySettings = gapAnalysisViews.find(view => view.id === selection.value)?.settings

    // Managers computation
    const viewsManagers = gapAnalysisViews.map(view => {
      return { viewTitle: view.title, managers: view.displayMetadata?.managers || [] }
    })
    const managers = viewsManagers.find(viewPair => viewPair.viewTitle === selection.text)?.managers || []

    let viewColumns =
      gapAnalysisSliceFilters.columns.filter(column =>
        selectedViewDisplaySettings?.selectedColumns?.includes(column.value)
      ) ?? gapAnalysisSliceFilters.columns

    if (selectedViewDisplaySettings?.isManagerColumnSelected) {
      viewColumns = [{ weight: 0, text: fieldNames.MANAGERS, value: fieldNames.MANAGERS }, ...viewColumns]
    }

    // step 3: dispatches for filter updates as well
    dispatch(
      gapAnalysisFiltersActions.setViewFilters({
        columns: sortBy(viewColumns, column => column.weight),
        competencies:
          selectedViewFilters.competencies?.map(c => {
            return { text: c.name ?? '', value: c.id ?? '' }
          }) ?? [],
        hierarchies: selectedViewFilters.hierarchies ?? [],
        hierarchyViewMode: {
          text: selectedViewFilters.matchOption === 'MATCH_ALL' ? 'All' : 'Any',
          value: selectedViewFilters.matchOption === 'MATCH_ALL' ? 'all' : 'any'
        },
        managers:
          managers.map(m => {
            return { text: m.fullName ?? '', value: m.id ?? '' }
          }) ?? [],
        occupations:
          selectedViewFilters.occupations?.map(o => {
            return { text: o, value: o }
          }) ?? [],
        people:
          selectedViewFilters.people?.map(p => {
            return { text: p.fullName ?? '', value: p.id ?? '' }
          }) ?? [],
        showAllReports: selectedViewFilters.showAllReports ?? false,
        showGridSeparator: selectedViewDisplaySettings?.showGridlines ?? true,
        gridlinesFrequency: selectedViewDisplaySettings?.gridlineFrequency ?? 1,
        showManagersColumn: selectedViewDisplaySettings?.isManagerColumnSelected ?? false
      } satisfies GAViewFilters)
    )
  }

  return {
    handleRemoveSelectedGAView: handleRemoveSelectedGAView,
    handleSelectGAView: handleSelectGAView
  }
}
